import { useCallback, useEffect, useRef, useState } from "react"
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom"
import { ArrowLeft, Lock, Pencil, Trash2 } from "lucide-react"
import { toast } from "sonner"

import { DETAIL_UNLOCK_FOR_STAGE } from "@/components/dashboard/data/deals"
import { CONCEPT_STRATEGIES } from "@/components/deals/concept/concept-data"
import { DealAskRobPanel } from "@/components/deals/DealAskRobPanel"
import { DealAppraisalTab } from "@/components/deals/DealAppraisalTab"
import { DealConceptTab } from "@/components/deals/DealConceptTab"
import { DealEnrichmentTab } from "@/components/deals/DealEnrichmentTab"
import { DealNegotiationTab } from "@/components/deals/DealNegotiationTab"
import { DealOverviewTab } from "@/components/deals/DealOverviewTab"
import { DealSnapshot } from "@/components/deals/DealSnapshot"
import { AppHeader } from "@/components/dashboard/AppHeader"
import { DealVendorTab } from "@/components/deals/DealVendorTab"
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useDashboardGuard } from "@/lib/activation"
import { activationPostcodeOptions } from "@/lib/activation-postcode-targets"
import { useActivation } from "@/lib/activation-context"
import { roceTargetFromCriteria } from "@/lib/appraisal-defaults"
import { useDealPipeline } from "@/lib/deal-pipeline-context"
import { cn } from "@/lib/utils"
import { useDealAskRob } from "@/hooks/use-deal-ask-rob"

const TABS = [
  { id: "overview", label: "Overview", minStage: 0 },
  { id: "enrichment", label: "Enrichment", minStage: 1 },
  { id: "vendor", label: "Vendor", minStage: 2 },
  { id: "concept", label: "Concept", minStage: 3 },
  { id: "appraisal", label: "Appraisal", minStage: 4 },
  { id: "negotiation", label: "Negotiation", minStage: 5 },
]

const CONTENT_WIDTH_BY_TAB = {
  overview: "max-w-5xl",
  enrichment: "max-w-[1400px]",
  vendor: "max-w-[1000px]",
  concept: "max-w-[1200px]",
  appraisal: "max-w-[1200px]",
  negotiation: "max-w-[1200px]",
}

const APPRAISAL_STAGES = ["Identified", "Appraising"]
const NEGOTIATION_UPDATE_STAGES = [
  "Identified",
  "Appraising",
  "Offer Ready",
  "In Negotiation",
  "Follow Up",
  "Legal / Due Diligence",
]

function tabForId(id) {
  return TABS.find((tab) => tab.id === id)
}

function isTabUnlocked(tabId, maxUnlockedStage) {
  const tab = tabForId(tabId)

  return Boolean(tab && maxUnlockedStage >= tab.minStage)
}

function tabFromSearch(searchParams, maxUnlockedStage) {
  const requestedTab = searchParams.get("tab")
  if (!isTabUnlocked(requestedTab, maxUnlockedStage)) return "overview"

  return requestedTab
}

function unlockLevelForDeal(deal) {
  if (!deal) return 1

  return deal.detailUnlockStage ?? DETAIL_UNLOCK_FOR_STAGE[deal.stage] ?? 1
}

function selectedStrategyForDeal(deal) {
  if (!deal) return null
  if (deal.selectedStrategy) return deal.selectedStrategy
  if (unlockLevelForDeal(deal) >= 4) return CONCEPT_STRATEGIES[0] ?? null

  return null
}

function appraisalResultsForDeal(deal) {
  return deal?.appraisalResults ?? deal?.appraisalSummary ?? null
}

function localNegotiationStatusForDeal(deal) {
  return deal?.negotiationStatus ?? deal?.negotiationSummary ?? null
}

function negotiationSummaryForDeal(deal, maxUnlockedStage) {
  if (maxUnlockedStage < 5) return null

  const persistedStatus = localNegotiationStatusForDeal(deal)
  const status = persistedStatus?.status ?? deal.isWonOrLost ?? deal.status
  if (!status) return null

  return {
    ...persistedStatus,
    status,
  }
}

function internalSuggestionPath(path) {
  if (typeof path !== "string" || !path.startsWith("/")) return null

  return path
}

export function DealDetailScreen() {
  const { dealId } = useParams()

  return <DealDetailContent key={dealId ?? "missing"} dealId={dealId} />
}

function DealDetailContent({ dealId }) {
  const guard = useDashboardGuard()
  const activation = useActivation()
  const postcodeOptions = activationPostcodeOptions(activation.step3)
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const {
    deleteDeal,
    dismissPriorityAction,
    findDeal,
    refreshDeals,
    replaceDeal,
    snoozePriorityAction,
    summary,
    updateDeal,
    isLoading,
  } = useDealPipeline()
  const deal = findDeal(dealId)
  const dealPriorityActions = deal?.priorityActions?.length
    ? deal.priorityActions
    : (summary.dailyPriorities ?? []).filter((priority) => priority.dealId === deal?.id)
  const maxUnlockedStage = unlockLevelForDeal(deal)
  const contentScrollRef = useRef(null)
  const activeTab = tabFromSearch(searchParams, maxUnlockedStage)
  const activeConceptView = searchParams.get("concept_step")
  const [selectedStrategy, setSelectedStrategy] = useState(() => selectedStrategyForDeal(deal))
  const [appraisalResults, setAppraisalResults] = useState(() => appraisalResultsForDeal(deal))
  const [negotiationStatus, setNegotiationStatus] = useState(() => localNegotiationStatusForDeal(deal))
  const [editOpen, setEditOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [nextAddress, setNextAddress] = useState("")
  const [nextActivationPostcodeTargetId, setNextActivationPostcodeTargetId] = useState("")
  const [nextSourceLink, setNextSourceLink] = useState("")
  const [deleteConfirmation, setDeleteConfirmation] = useState("")
  const [robDrawerOpen, setRobDrawerOpen] = useState(false)
  const [actionError, setActionError] = useState("")
  const [isSavingDeal, setIsSavingDeal] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  useEffect(() => {
    if (contentScrollRef.current) contentScrollRef.current.scrollTop = 0
  }, [activeTab])

  const handleConceptViewChange = useCallback((nextView) => {
    setSearchParams((currentParams) => {
      const nextParams = new URLSearchParams(currentParams)
      nextParams.set("tab", "concept")

      if (nextView === "thesis") {
        nextParams.delete("concept_step")
      } else {
        nextParams.set("concept_step", nextView)
      }

      return nextParams
    })
  }, [setSearchParams])

  const handleAskRobSuggestionAccepted = ({ deal: persistedDeal, suggestion } = {}) => {
    if (persistedDeal) replaceDeal(persistedDeal)
    void refreshDeals?.()

    const path = internalSuggestionPath(suggestion?.payload?.path)
    if (path) navigate(path)
  }

  const askRob = useDealAskRob({
    deal,
    tab: activeTab,
    onSuggestionAccepted: handleAskRobSuggestionAccepted,
  })

  if (guard) return guard

  if (isLoading) return <LoadingState />
  if (!deal) return <NotFoundState />

  const displayedSelectedStrategy = selectedStrategy ?? selectedStrategyForDeal(deal)
  const displayedAppraisalResults = appraisalResults ?? appraisalResultsForDeal(deal)
  const displayedNegotiationStatus =
    negotiationStatus ??
    negotiationSummaryForDeal(deal, maxUnlockedStage)

  const updateTabSearch = (nextTab) => {
    setSearchParams((currentParams) => {
      const nextParams = new URLSearchParams(currentParams)
      if (nextTab === "overview") {
        nextParams.delete("tab")
      } else {
        nextParams.set("tab", nextTab)
      }

      return nextParams
    })
  }

  const unlockStage = (stage, nextTab, updates = {}, message = null) => {
    const wasLocked = maxUnlockedStage < stage
    updateDeal(deal.id, {
      ...updates,
      detailUnlockStage: Math.max(maxUnlockedStage, stage),
    }, { activitySource: "system" })
    if (wasLocked && message) toast.success(message)
    if (nextTab) {
      updateTabSearch(nextTab)
    }
  }

  const handleTabChange = (nextTab) => {
    if (!isTabUnlocked(nextTab, maxUnlockedStage)) return

    updateTabSearch(nextTab)
    if (nextTab === "overview") void refreshDeals()
  }

  const handleVendorComplete = () => {
    unlockStage(3, null, {}, "Concept tab unlocked — vendor intelligence logged")
  }

  const handleRunAppraisal = (strategy) => {
    setSelectedStrategy(strategy)
    const updates = APPRAISAL_STAGES.includes(deal.stage)
      ? { stage: "Appraising", status: "Action Required" }
      : {}
    unlockStage(4, "appraisal", { ...updates, selectedStrategy: strategy }, "Appraisal unlocked — strategy selected")
  }

  const handleConceptSelected = ({ deal: persistedDeal, strategy }) => {
    if (strategy) setSelectedStrategy(strategy)
    if (persistedDeal) replaceDeal(persistedDeal)
  }

  const handleCalculate = (maxPrice, targetClose, result) => {
    const nextResults = result ?? { maxPrice, targetClose }
    setAppraisalResults(nextResults)
    unlockStage(
      5,
      null,
      {
        stage: "Offer Ready",
        status: "Action Required",
        appraisalSummary: nextResults,
      },
      "Negotiation unlocked — appraisal complete",
    )
  }

  const handleAppraisalSaved = ({ deal: persistedDeal, result }) => {
    if (result) setAppraisalResults(result)
    if (persistedDeal) replaceDeal(persistedDeal)
  }

  const handleNegotiationUpdate = (status, latestOffer, walkAway) => {
    const nextStatus = { status, latestOffer, walkAway }
    setNegotiationStatus(nextStatus)
    if (NEGOTIATION_UPDATE_STAGES.includes(deal.stage)) {
      updateDeal(deal.id, {
        stage: "In Negotiation",
        status: "Active",
        detailUnlockStage: 5,
        negotiationStatus: nextStatus,
      }, { activitySource: "manual" })
    }
  }

  const handleNegotiationOutcome = (updates = {}) => {
    const nextStatus = updates.negotiationStatus ?? (
      updates.status
        ? {
            status: updates.status,
            latestOffer: updates.latestOffer,
            walkAway: updates.walkAway,
          }
        : null
    )

    if (nextStatus) setNegotiationStatus(nextStatus)

    updateDeal(deal.id, {
      ...updates,
      detailUnlockStage: Math.max(maxUnlockedStage, 5),
    }, { activitySource: "manual" })
  }

  const handleNegotiationSaved = ({ deal: persistedDeal, negotiationStatus: nextStatus } = {}) => {
    if (nextStatus) setNegotiationStatus(nextStatus)
    if (persistedDeal) replaceDeal(persistedDeal)
  }

  const openEditDialog = () => {
    setActionError("")
    setNextAddress(deal.address)
    setNextActivationPostcodeTargetId(deal.activationPostcodeTargetId ?? "")
    setNextSourceLink(deal.sourceLink ?? deal.sourceUrl ?? "")
    setEditOpen(true)
  }

  const handleEditSubmit = async (event) => {
    event.preventDefault()
    const address = nextAddress.trim()
    const sourceLink = nextSourceLink.trim()
    if (!address) return

    setActionError("")
    setIsSavingDeal(true)
    try {
      await updateDeal(deal.id, {
        address,
        activationPostcodeTargetId: nextActivationPostcodeTargetId,
        sourceLink,
      }, { activitySource: "manual" })
      setEditOpen(false)
    } catch (error) {
      setActionError(error.message)
    } finally {
      setIsSavingDeal(false)
    }
  }

  const openDeleteDialog = () => {
    setActionError("")
    setDeleteConfirmation("")
    setDeleteOpen(true)
  }

  const handleDeleteSubmit = async (event) => {
    event.preventDefault()
    if (deleteConfirmation !== "DELETE") return

    setActionError("")
    setIsDeleting(true)
    try {
      await deleteDeal(deal.id, { confirmation: deleteConfirmation })
      navigate("/deal-pipeline")
    } catch (error) {
      setActionError(error.message)
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <main className="fixed inset-0 flex h-screen min-h-screen flex-col overflow-hidden bg-background text-foreground">
      <header className="border-b border-border bg-card px-5 pt-3">
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label="Back to pipeline"
            onClick={() => navigate("/deal-pipeline")}
            className="shrink-0"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex min-w-0 items-center gap-3">
            <h1 className="truncate text-lg font-bold tracking-normal text-foreground">
              Mission Control
            </h1>
            <span
              title={deal.address}
              className="max-w-48 truncate rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-semibold tracking-wide text-primary sm:max-w-md"
            >
              {deal.address}
            </span>
          </div>
          <div className="ml-auto flex shrink-0 items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label="Edit deal"
              onClick={openEditDialog}
              className="bg-deal-edit text-foreground hover:bg-deal-edit/90 hover:text-foreground"
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="destructive"
              size="icon-sm"
              aria-label="Delete deal"
              onClick={openDeleteDialog}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <div className="-mx-5 mt-4 overflow-x-auto px-5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <TabsList
              variant="line"
              className="-mb-px h-auto min-w-max justify-start gap-8 rounded-none border-0 p-0"
            >
              {TABS.map((tab) => {
                const locked = maxUnlockedStage < tab.minStage

                return (
                  <TabsTrigger
                    key={tab.id}
                    value={tab.id}
                    disabled={locked}
                    className={cn(
                      "relative h-10 flex-none grow-0 basis-auto rounded-none border-0 bg-transparent px-0 pb-3 pt-0 text-sm font-medium text-muted-foreground shadow-none after:bg-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none",
                      locked && "cursor-not-allowed text-muted-foreground/50",
                    )}
                  >
                    {tab.label}
                    {locked ? <Lock aria-hidden="true" className="ml-1.5 h-3.5 w-3.5" /> : null}
                  </TabsTrigger>
                )
              })}
            </TabsList>
          </div>
        </Tabs>
      </header>

      <section ref={contentScrollRef} className="min-h-0 flex-1 overflow-y-auto bg-muted/20">
        <div
          className={cn(
            "mx-auto flex w-full flex-col gap-8 px-4 py-8 md:px-8",
            CONTENT_WIDTH_BY_TAB[activeTab] ?? "max-w-5xl",
          )}
        >
          <Tabs value={activeTab} onValueChange={handleTabChange}>
            <TabsContent value="overview">
              <div className="flex flex-col gap-8">
                <DealSnapshot deal={deal} />
                <DealOverviewTab
                  deal={deal}
                  maxUnlockedStage={maxUnlockedStage}
                  selectedStrategy={displayedSelectedStrategy}
                  appraisalResults={displayedAppraisalResults}
                  negotiationStatus={displayedNegotiationStatus}
                  priorityActions={dealPriorityActions}
                  onDismissPriority={dismissPriorityAction}
                  askRob={askRob}
                  onProgressStepClick={handleTabChange}
                  onSnoozePriority={snoozePriorityAction}
                  onSuggestionAccepted={handleAskRobSuggestionAccepted}
                />
              </div>
            </TabsContent>
            <TabsContent value="enrichment">
              <DealEnrichmentTab
                deal={deal}
                onComplete={() => unlockStage(2, null, {}, "Vendor tab unlocked — complete enrichment detected")}
                onDealChange={replaceDeal}
              />
            </TabsContent>
            <TabsContent value="vendor">
              <DealVendorTab deal={deal} onComplete={handleVendorComplete} />
            </TabsContent>
            <TabsContent value="concept">
              <DealConceptTab
                deal={deal}
                selectedStrategy={displayedSelectedStrategy}
                conceptView={activeConceptView}
                onConceptViewChange={handleConceptViewChange}
                onConceptSelected={handleConceptSelected}
                onRunAppraisal={handleRunAppraisal}
              />
            </TabsContent>
            <TabsContent value="appraisal">
              <DealAppraisalTab
                deal={deal}
                selectedStrategy={displayedSelectedStrategy}
                appraisalResults={displayedAppraisalResults}
                marketData={activation.step4.marketData}
                defaultTargetReturn={roceTargetFromCriteria(activation.step5)}
                onCalculate={handleCalculate}
                onAppraisalSaved={handleAppraisalSaved}
              />
            </TabsContent>
            <TabsContent value="negotiation">
              <DealNegotiationTab
                deal={deal}
                negotiationStatus={displayedNegotiationStatus}
                onLogUpdate={handleNegotiationUpdate}
                onLogOutcome={handleNegotiationOutcome}
                onNegotiationSaved={handleNegotiationSaved}
              />
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {activeTab === "overview" ? null : (
        <Button
          type="button"
          variant="ghost"
          aria-label="Open Rob deal mentor"
          onClick={() => setRobDrawerOpen(true)}
          className="fixed bottom-14 right-14 z-40 h-14 w-14 rounded-full border border-border bg-card p-1.5 shadow-xl hover:bg-card hover:shadow-2xl md:bottom-16 md:right-16"
        >
          <img
            src="/rob_headshot.jpeg"
            alt=""
            aria-hidden="true"
            className="h-full w-full rounded-full object-cover"
          />
          <span className="absolute bottom-1 right-1 h-3.5 w-3.5 rounded-full border-2 border-card bg-primary" />
        </Button>
      )}

      <Dialog open={robDrawerOpen} onOpenChange={setRobDrawerOpen}>
        <DialogContent
          showCloseButton
          className="left-auto right-0 top-[5.5rem] flex h-[calc(100vh-5.5rem)] max-w-full translate-x-0 translate-y-0 flex-col gap-0 overflow-hidden rounded-none border-y-0 border-r-0 p-0 shadow-xl sm:max-w-md"
          overlayClassName="bg-background/40"
        >
          <div className="flex min-h-0 flex-1 flex-col bg-card text-card-foreground">
            <DialogHeader className="border-b border-border p-6">
              <div className="flex items-center gap-2">
                <img
                  src="/rob_headshot.jpeg"
                  alt="Rob"
                  className="h-6 w-6 rounded-full object-cover"
                />
                <DialogTitle className="text-sm font-bold uppercase tracking-widest">
                  Rob
                </DialogTitle>
              </div>
              <DialogDescription>
                Ask Rob anything about this deal.
              </DialogDescription>
            </DialogHeader>
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-muted/20 p-6">
              <DealAskRobPanel askRob={askRob} fullHeight variant="drawer" />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <form onSubmit={handleEditSubmit} className="grid gap-4">
            <DialogHeader>
              <DialogTitle>Edit deal</DialogTitle>
              <DialogDescription>
                Update the property address and source link.
              </DialogDescription>
            </DialogHeader>
            <label className="grid gap-2 text-sm font-medium text-foreground">
              Property address
              <Input
                value={nextAddress}
                onChange={(event) => setNextAddress(event.target.value)}
                disabled={isSavingDeal}
                autoFocus
              />
            </label>
            <label className="grid gap-2 text-sm font-medium text-foreground">
              Deal postcode area
              <select
                value={nextActivationPostcodeTargetId}
                onChange={(event) => setNextActivationPostcodeTargetId(event.target.value)}
                disabled={isSavingDeal || postcodeOptions.length === 0}
                className="h-9 w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-medium text-foreground shadow-xs outline-none transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="">
                  {postcodeOptions.length === 0 ? "No activation areas available" : "Select postcode area"}
                </option>
                {postcodeOptions.map((target) => (
                  <option key={target.id} value={target.id}>{target.postcode}</option>
                ))}
              </select>
            </label>
            <label className="grid gap-2 text-sm font-medium text-foreground">
              Source link
              <Input
                value={nextSourceLink}
                onChange={(event) => setNextSourceLink(event.target.value)}
                disabled={isSavingDeal}
              />
            </label>
            {actionError ? <p className="text-sm text-destructive">{actionError}</p> : null}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={!nextAddress.trim() || !nextActivationPostcodeTargetId || isSavingDeal}>
                Save changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <form onSubmit={handleDeleteSubmit} className="grid gap-4">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete deal</AlertDialogTitle>
              <AlertDialogDescription>
                This permanently removes the deal and related pipeline data.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <label className="grid gap-2 text-sm font-medium text-foreground">
              Type DELETE to confirm
              <Input
                value={deleteConfirmation}
                onChange={(event) => setDeleteConfirmation(event.target.value)}
                disabled={isDeleting}
              />
            </label>
            {actionError ? <p className="text-sm text-destructive">{actionError}</p> : null}
            <AlertDialogFooter>
              <AlertDialogCancel type="button" disabled={isDeleting}>
                Cancel
              </AlertDialogCancel>
              <Button
                type="submit"
                variant="destructive"
                disabled={deleteConfirmation !== "DELETE" || isDeleting}
              >
                Permanently delete deal
              </Button>
            </AlertDialogFooter>
          </form>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  )
}

function LoadingState() {
  return (
    <main className="min-h-screen bg-background text-foreground">
      <AppHeader active="deal-pipeline" status="online" planName="Pro" showNav />
      <section className="mx-auto flex min-h-[calc(100vh-4rem)] w-full max-w-[1600px] items-center justify-center px-4 py-8 md:px-6 lg:px-8">
        <div className="w-full max-w-md rounded-lg border border-border bg-card p-6 text-center text-card-foreground shadow-sm">
          <h1 className="text-2xl font-bold tracking-normal text-foreground">
            Loading Deal
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Fetching the latest deal pipeline state.
          </p>
        </div>
      </section>
    </main>
  )
}

function NotFoundState() {
  return (
    <main className="min-h-screen bg-background text-foreground">
      <AppHeader active="deal-pipeline" status="online" planName="Pro" showNav />
      <section className="mx-auto flex min-h-[calc(100vh-4rem)] w-full max-w-[1600px] items-center justify-center px-4 py-8 md:px-6 lg:px-8">
        <div className="w-full max-w-md rounded-lg border border-border bg-card p-6 text-center text-card-foreground shadow-sm">
          <h1 className="text-2xl font-bold tracking-normal text-foreground">
            Deal Not Found
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            This deal is not available in the current pipeline.
          </p>
          <Button asChild className="mt-5">
            <Link to="/deal-pipeline">Return to Pipeline</Link>
          </Button>
        </div>
      </section>
    </main>
  )
}
